/**
 * Where to put the clue card inside the live preview.
 *
 * Every theme lays its clue screen out differently (the card sits higher or
 * lower, some have a header above it), so a fixed crop of the middle of the
 * phone cut some cards and left empty space under others. Instead the preview
 * measures the card and frames it: scaled to the preview width with a margin,
 * never above life size, capped at a maximum height, centred.
 */

/**
 * Finds the clue card: the painted container that holds the question, the
 * answer field and the button. Some themes paint a box around the field and the
 * button only, so the question has to be part of the search or it gets cut off.
 * With no card around all three, it frames the three of them together.
 */
export function findCardBox(stage, question = "") {
  const field = stage.querySelector("input, textarea");
  if (!field) return null;
  const button = stage.querySelector("button");
  const wanted = String(question || "").trim();
  const questionEl = wanted
    ? [...stage.querySelectorAll("*")].find((el) => el.children.length === 0 && el.textContent.includes(wanted))
    : null;
  const parts = [field, button, questionEl].filter(Boolean);

  const base = stage.getBoundingClientRect();
  const scale = base.width / stage.offsetWidth || 1;
  const toStage = (r) => ({ x: (r.left - base.left) / scale, y: (r.top - base.top) / scale, w: r.width / scale, h: r.height / scale });

  let node = field;
  while (node && node !== stage && !parts.every((el) => node.contains(el))) node = node.parentElement;
  while (node && node !== stage) {
    const r = node.getBoundingClientRect();
    const cs = window.getComputedStyle(node);
    const painted =
      (cs.backgroundColor && cs.backgroundColor !== "rgba(0, 0, 0, 0)" && cs.backgroundColor !== "transparent") ||
      (cs.backgroundImage && cs.backgroundImage !== "none") ||
      (cs.boxShadow && cs.boxShadow !== "none") ||
      parseFloat(cs.borderTopWidth) > 0;
    // The card, not the whole screen behind it.
    if (painted && r.height / scale < stage.offsetHeight * 0.8) return toStage(r);
    node = node.parentElement;
  }
  const rects = parts.map((e) => e.getBoundingClientRect());
  const left = Math.min(...rects.map((r) => r.left)), right = Math.max(...rects.map((r) => r.right));
  const top = Math.min(...rects.map((r) => r.top)), bottom = Math.max(...rects.map((r) => r.bottom));
  return toStage({ left, top, width: right - left, height: bottom - top });
}

/**
 * @param {{x:number,y:number,w:number,h:number}} box  the card, in phone pixels
 * @param {{width:number,maxHeight:number,pad:number,padTop?:number}} opts
 * @returns {{scale:number,height:number,offsetX:number,translateX:number,translateY:number}}
 */
export function frameBox(box, { width, maxHeight, pad, padTop = pad }) {
  const w = box.w + pad * 2;
  const h = box.h + padTop + pad;
  const scale = Math.min(1, width / w, maxHeight / h);
  return {
    scale,
    height: h * scale,
    offsetX: (width - w * scale) / 2,
    translateX: -(box.x - pad),
    translateY: -(box.y - padTop),
  };
}
